import React, { useState, useEffect, useMemo } from "react";
import { Table, Button, Space, Input, Row, Col, Modal, Form, Select, message, Upload } from "antd";
import { PrinterOutlined, PlusOutlined, UploadOutlined, FilePdfOutlined, FileExcelOutlined, DeleteOutlined } from "@ant-design/icons";
import api, { ASSETS_BASE_URL } from "../api";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const Santha = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [selectedSanthaFilter, setSelectedSanthaFilter] = useState(null);
  const [santhaOptions, setSanthaOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const yearsub = Form.useWatch("yearsub", form);
  const meetingfee = Form.useWatch("meetingfee", form);
  const adminFee = Form.useWatch("Adminfee", form);
  const selectedMemberId = Form.useWatch("memberId", form);
  const santhaType = Form.useWatch("santhaname", form);

  // Memoize filtered transactions
  const filteredTransactions = useMemo(
    () =>
      data.filter(
        (record) =>
          record.memberId?._id === selectedMemberId &&
          record.santhaname === santhaType
      ),
    [data, selectedMemberId, santhaType]
  );

  useEffect(() => {
    fetchData();
    fetchMembers();
    fetchSanthaMaster();
  }, []);

  useEffect(() => {
    if (!selectedMemberId) {
      form.setFieldsValue({ groupincharge: "", outstanding: "0.00" });
      return;
    }

    const member = memberOptions.find((m) => m.value === selectedMemberId);
    const latestTransaction = filteredTransactions.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    )[0];

    const initialOutstanding = form.getFieldValue("tobeCollect") || "0.00";
    const previousOutstanding = latestTransaction
      ? parseFloat(latestTransaction.outstanding)
      : parseFloat(initialOutstanding);

    const outstanding = (
      previousOutstanding -
      ((parseFloat(adminFee) || 0) + (parseFloat(yearsub) || 0) + (parseFloat(meetingfee) || 0))
    ).toFixed(2);

    form.setFieldsValue({
      groupincharge: member?.groupincharge || "",
      outstanding: isNaN(outstanding) ? "0.00" : outstanding,
    });
  }, [selectedMemberId, santhaType, memberOptions, adminFee, yearsub, meetingfee, form, filteredTransactions]);

  const fetchSanthaMaster = async () => {
    try {
      const response = await api.get("/member/getsanthamaster");
      setSanthaOptions(response.data.data.map((s) => ({ value: s._id, label: s.santhaname })));
    } catch (error) {
      message.error("Failed to fetch Santha master data.");
    }
  };

  const fetchData = async () => {
    try {
      const response = await api.get("/member/getsantha");
      const sortedData = (response.data.data || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setData(sortedData);
      setFilteredData(sortedData);
    } catch (error) {
      message.error("Failed to fetch transaction data.");
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await api.get("/member/get-all-members");
      const sortedMembers = response.data.data
        .filter((m) => m.Accno && m.Name)
        .sort((a, b) => {
          const numA = parseInt(a.Accno.replace(/\D/g, ""), 10);
          const numB = parseInt(b.Accno.replace(/\D/g, ""), 10);
          return numA - numB;
        });

      setMemberOptions(
        sortedMembers.map((m) => ({
          value: m._id,
          label: `${m.Accno} (${m.Name})`,
          groupincharge: m.groupincharge || "",
        }))
      );
    } catch (error) {
      message.error("Failed to fetch member data.");
    }
  };

  // Enhanced search functionality
  useEffect(() => {
    if (!Array.isArray(data)) return;

    let filtered = [...data];

    // Apply Santha filter if selected
    if (selectedSanthaFilter) {
      filtered = filtered.filter((item) => item.santhaname?.toString() === selectedSanthaFilter);
    }

    // Apply search filter
    if (searchText.trim()) {
      const search = searchText.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        // Get member details from memberOptions
        const member = memberOptions.find((m) => m.value === item.memberId?._id);
        const santha = santhaOptions.find((s) => s.value === item.santhaname);

        // Create an array of all searchable values, including nested and derived fields
        const searchableValues = [
          item.memberId?.Accno || "", // ACCI Code
          member?.label || "", // Member Name (from memberOptions)
          member?.groupincharge || "", // Group Incharge (from memberOptions)
          santha?.label || "", // Santha Type (from santhaOptions)
          item.Adminfee?.toString() || "", // Admin Fee
          item.yearsub?.toString() || "", // Year Subscription
          item.meetingfee?.toString() || "", // Meeting Fee
          item.other?.toString() || "", // Other
          item.tobeCollect?.toString() || "", // To Be Collect
          item.outstanding?.toString() || "", // Outstanding
          item.remarks || "", // Remarks
          item.invoice || "", // Invoice Number
        ];

        // Check if any searchable value includes the search text
        return searchableValues.some((value) =>
          value.toLowerCase().includes(search)
        );
      });
    }

    setFilteredData(filtered);
  }, [searchText, selectedSanthaFilter, data, memberOptions, santhaOptions]);

  const handleAddOrUpdate = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === "recipt" && values.recipt?.[0]?.originFileObj) {
          formData.append("recipt", values.recipt[0].originFileObj);
        } else if (key !== "groupincharge") {
          formData.append(key, values[key] ?? "");
        }
      });

      const response = await api({
        method: editMode ? "PUT" : "POST",
        url: editMode
          ? `/member/upsantha/${currentRecord._id}`
          : `/member/createsanta`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      message.success(editMode ? "Santha updated successfully" : "Santha added successfully");
      fetchData();
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error(`Failed to save Santha: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this Santha record?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await api.delete(`/member/delsantha/${id}`);
          message.success("Santha deleted successfully");
          fetchData();
        } catch (error) {
          message.error(`Failed to delete Santha: ${error.response?.data?.message || error.message}`);
        }
      },
    });
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Santha Transactions", 20, 10);
    const tableColumn = [
      "S.No",
      "ACCI Code",
      "Santha Type",
      "Member Name",
      "Group Incharge",
      "Admin Fee",
      "Year Subscription",
      "Meeting Fee",
      "Other",
      "Remarks",
    ];

    const sortedExportData = [...filteredData].sort((a, b) => {
      const accNoA = a.memberId?.Accno || "";
      const accNoB = b.memberId?.Accno || "";
      const numA = parseInt(accNoA.replace(/\D/g, ""), 10) || 0;
      const numB = parseInt(accNoB.replace(/\D/g, ""), 10) || 0;
      return numA - numB;
    });

    const tableRows = sortedExportData.map((record, index) => [
      index + 1,
      record.memberId?.Accno || "",
      santhaOptions.find((s) => s.value === record.santhaname)?.label || "",
      memberOptions.find((m) => m.value === record.memberId?._id)?.label || "",
      memberOptions.find((m) => m.value === record.memberId?._id)?.groupincharge || "",
      record.Adminfee || 0,
      record.yearsub || 0,
      record.meetingfee || 0,
      record.other || 0,
      record.remarks || "",
    ]);
    doc.autoTable({ head: [tableColumn], body: tableRows });
    doc.save("santha_transactions.pdf");
  };

  const exportExcel = () => {
    const sortedExportData = [...filteredData].sort((a, b) => {
      const accNoA = a.memberId?.Accno || "";
      const accNoB = b.memberId?.Accno || "";
      const numA = parseInt(accNoA.replace(/\D/g, ""), 10) || 0;
      const numB = parseInt(accNoB.replace(/\D/g, ""), 10) || 0;
      return numA - numB;
    });

    const excelData = sortedExportData.map((record, index) => {
      const member = memberOptions.find((m) => m.value === record.memberId?._id);
      return {
        "S.No": index + 1,
        "ACCI Code": record.memberId?.Accno || "",
        "Santha Type": santhaOptions.find((s) => s.value === record.santhaname)?.label || "",
        "Member Name": member?.label || "",
        "Group Incharge": member?.groupincharge || "",
        "Admin Fee": record.Adminfee || 0,
        "Year Subscription": record.yearsub || 0,
        "Meeting Fee": record.meetingfee || 0,
        "Other": record.other || 0,
        "Outstanding": record.outstanding || 0,
        "Remarks": record.remarks || "",
      };
    });

    const totalRow = {
      "S.No": "",
      "ACCI Code": "",
      "Santha Type": "",
      "Member Name": "Total",
      "Group Incharge": "",
      "Admin Fee": sortedExportData.reduce((sum, record) => sum + (parseFloat(record.Adminfee) || 0), 0).toFixed(2),
      "Year Subscription": sortedExportData.reduce((sum, record) => sum + (parseFloat(record.yearsub) || 0), 0).toFixed(2),
      "Meeting Fee": sortedExportData.reduce((sum, record) => sum + (parseFloat(record.meetingfee) || 0), 0).toFixed(2),
      "Other": sortedExportData.reduce((sum, record) => sum + (parseFloat(record.other) || 0), 0).toFixed(2),
      "Outstanding": sortedExportData.reduce((sum, record) => sum + (parseFloat(record.outstanding) || 0), 0).toFixed(2),
      "Remarks": "",
    };

    const worksheet = XLSX.utils.json_to_sheet([...excelData, totalRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Santha Transactions");
    XLSX.writeFile(workbook, "santha_transactions.xlsx");
  };

  const numberToWords = (num) => {
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const units = ["", "Thousand", "Million", "Billion"];

    if (num === 0) return "Zero";

    const convertLessThanThousand = (n) => {
      if (n === 0) return "";
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return `${tens[Math.floor(n / 10)]} ${ones[n % 10]}`.trim();
      return `${ones[Math.floor(n / 100)]} Hundred ${convertLessThanThousand(n % 100)}`.trim();
    };

    const integerPart = Math.floor(num);
    let words = "";
    let n = integerPart;

    if (n === 0) words = "Zero";
    else {
      let i = 0;
      while (n > 0) {
        if (n % 1000 !== 0) {
          words = `${convertLessThanThousand(n % 1000)} ${units[i]} ${words}`.trim();
        }
        n = Math.floor(n / 1000);
        i++;
      }
    }

    return words;
  };

  const getBase64Image = async (url) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Failed to convert image to Base64"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error(`Failed to fetch image from ${url}:`, error);
      return null;
    }
  };

  const printPDF = async (record) => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();

    const acciLogoUrl = "/logo.png";
    const acciLogo = await getBase64Image(acciLogoUrl);
    if (acciLogo) {
      const imgWidth = 30;
      const imgHeight = 30;
      doc.addImage(acciLogo, "PNG", 20, 10, imgWidth, imgHeight);
    }

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("AGAMUDAYAR", pageWidth / 2, 18, { align: "center" });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("CHAMBER OF COMMERCE AND INDUSTRIES", pageWidth / 2, 25, { align: "center" });

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Santha Transaction Invoice", pageWidth / 2, 40, { align: "center" });

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 50);
    doc.text(`Invoice No: ${record.invoice || ""}`, 20, 56);

    const member = memberOptions.find((m) => m.value === record.memberId?._id);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Member Details:", 20, 65);

    doc.setFont("helvetica", "normal");
    doc.text(`ACCI Code: ${record.memberId?.Accno || ""}`, 20, 72);
    doc.text(`Member Name: ${member?.label || ""}`, 20, 79);
    doc.text(`Group Incharge: ${member?.groupincharge || ""}`, 20, 86);
    doc.text(`Santha Type: ${santhaOptions.find((s) => s.value === record.santhaname)?.label || ""}`, 20, 93);

    const adminFee = parseFloat(record.Adminfee) || 0;
    const yearSub = parseFloat(record.yearsub) || 0;
    const meetingFee = parseFloat(record.meetingfee) || 0;
    const other = parseFloat(record.other) || 0;
    const totalCollection = (adminFee + yearSub + meetingFee + other).toFixed(2);

    const tableColumn = ["Description", "Amount"];
    const tableRows = [
      ["Admin Fee", adminFee.toFixed(2)],
      ["Year Subscription", yearSub.toFixed(2)],
      ["Meeting Fee", meetingFee.toFixed(2)],
      ["Other", other.toFixed(2)],
      ["Total Collection", totalCollection],
      ["To Be Collect", (parseFloat(record.tobeCollect) || 0).toFixed(2)],
      ["Outstanding", (parseFloat(record.outstanding) || 0).toFixed(2)],
    ];
    if (record.remarks) {
      tableRows.push(["Remarks", record.remarks]);
    }

    const tableStartY = 100;
    doc.autoTable({
      startY: tableStartY,
      head: [tableColumn],
      body: tableRows,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      margin: { left: 20, right: 20 },
    });

    const finalY = doc.lastAutoTable.finalY || tableStartY + 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`In words: ${numberToWords(parseFloat(totalCollection))} Only`, 20, finalY + 10);

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageHeight = doc.internal.pageSize.height;

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text("Thank you for your payment!", pageWidth / 2, pageHeight - 40, { align: "center" });

      doc.setFontSize(8);
      doc.text("Administrative Office", 20, pageHeight - 24);
      doc.text("Plot No - 5 West, Marthupandiyar 2nd Cross Street,", 20, pageHeight - 20);
      doc.text("Viswanathapuram, Madurai - 625 014", 20, pageHeight - 16);
      doc.text("Phone: +91 96000 35625", 20, pageHeight - 12);
      doc.text(`Page ${i} of ${pageCount}`, 180, pageHeight - 12);
    }

    const pdfBlob = doc.output("blob");
    const pdfURL = URL.createObjectURL(pdfBlob);
    window.open(pdfURL, "_blank");
  };

  const printPDFA5 = async (record) => {
    const member = memberOptions.find((m) => m.value === record.memberId?._id);
    const santhaTypeLabel = santhaOptions.find((s) => s.value === record.santhaname)?.label || "";

    // CALCULATIONS
    const adminFee = parseFloat(record.Adminfee) || 0;
    const yearSub = parseFloat(record.yearsub) || 0;
    const meetingFee = parseFloat(record.meetingfee) || 0;
    const other = parseFloat(record.other) || 0;
    const totalCollection = adminFee + yearSub + meetingFee + other;
    const toBeCollect = parseFloat(record.tobeCollect) || 0;
    const outstanding = parseFloat(record.outstanding) || 0;

    // TABLE ROWS
    const tableColumn = ["Description", "Amount"];
    const tableRows = [
      ["Admin Fee", adminFee.toFixed(2)],
      ["Year Sub", yearSub.toFixed(2)],
      ["Meeting Fee", meetingFee.toFixed(2)],
      ["Other", other.toFixed(2)],
      ["Total Collect", totalCollection.toFixed(2)],
      ["To Be Collect", toBeCollect.toFixed(2)],
      ["Outstanding", outstanding.toFixed(2)],
    ];

    if (record.remarks && record.remarks.trim()) {
      tableRows.push(["Remarks", record.remarks.trim()]);
    }

    // DYNAMIC HEIGHT CALCULATION TO ELIMINATE EXTRA PAPER FEED
    const margin = 4; // Smaller margins (4mm) on left/right to maximize print space
    const pageWidth = 80;
    const contentWidth = pageWidth - (margin * 2);
    const splitWidth = contentWidth * 0.85; // Defensive wrapping width in mm (matching document unit) to prevent right-edge text overflow

    const tempDoc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [pageWidth, 150],
    });

    // CRITICAL: Set font properties on tempDoc to match the exact output document sizes
    tempDoc.setFont("helvetica", "bold");
    tempDoc.setFontSize(9); // Matches the new larger details size (9pt)

    const memberNameStr = `Name: ${member?.label || ""}`;
    const splitName = tempDoc.splitTextToSize(memberNameStr, splitWidth);

    const groupInchargeStr = `Group Incharge: ${member?.groupincharge || ""}`;
    const splitGroup = tempDoc.splitTextToSize(groupInchargeStr, splitWidth);

    tempDoc.setFont("helvetica", "bold");
    tempDoc.setFontSize(9.5); // Matches the new larger words size (9.5pt)
    const amountWords = `In words: Rupees ${numberToWords(Math.round(totalCollection))} Only`;
    const splitWords = tempDoc.splitTextToSize(amountWords, splitWidth);

    const tableRowsCount = tableRows.length;

    // Recalibrated height metrics for larger fonts: Base height of 88mm encompasses all margins, logo, headers, footer and table head
    const calculatedHeight = 88 + 
      (splitName.length * 3.8) + 
      (splitGroup.length * 3.8) + 
      (tableRowsCount * 4.6) + 
      (splitWords.length * 4.2);

    const receiptHeight = Math.max(120, Math.ceil(calculatedHeight) + 22); // Safe cut-size with 22mm safety padding to guarantee no footer cutoff

    // Instantiate jsPDF with the exact calculated paper height in mm
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [pageWidth, receiptHeight], 
    });

    let currentY = 6;

    // LOGO - Centered
    const acciLogoUrl = "/logo.png";
    const acciLogo = await getBase64Image(acciLogoUrl);
    if (acciLogo) {
      const imgWidth = 10;
      const imgHeight = 10;
      doc.addImage(acciLogo, "PNG", (pageWidth - imgWidth) / 2, currentY, imgWidth, imgHeight);
      currentY += imgHeight + 3;
    } else {
      currentY += 4;
    }

    // HEADER
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12); // Increased to 12pt for high visibility
    doc.text("AGAMUDAYAR", pageWidth / 2, currentY, { align: "center" });
    currentY += 4;

    doc.setFont("helvetica", "bold"); 
    doc.setFontSize(9); // Increased to 9pt
    doc.text("CHAMBER OF COMMERCE AND", pageWidth / 2, currentY, { align: "center" });
    currentY += 3;
    doc.text("INDUSTRIES", pageWidth / 2, currentY, { align: "center" });
    currentY += 5;

    // TITLE
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10); // Increased to 10pt
    doc.text("SANTHA TRANSACTION INVOICE", pageWidth / 2, currentY, { align: "center" });
    currentY += 5;

    // LINE DIVIDER
    doc.setLineWidth(0.2);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 4;

    // DATE & INVOICE
    doc.setFont("helvetica", "bold"); 
    doc.setFontSize(9); // Increased to 9pt
    doc.text(`Date: ${new Date().toLocaleDateString()}`, margin, currentY);
    doc.text(`Inv: ${record.invoice || ""}`, pageWidth - margin, currentY, { align: "right" });
    currentY += 4;

    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 4;

    // MEMBER DETAILS
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5); // Increased to 9.5pt
    doc.text("MEMBER DETAILS:", margin, currentY);
    currentY += 4;

    doc.setFont("helvetica", "bold"); 
    doc.setFontSize(9); // Increased to 9pt for bold, clear presentation
    
    doc.text(`ACCI Code: ${record.memberId?.Accno || ""}`, margin, currentY);
    currentY += 4;
    
    doc.text(splitName, margin, currentY);
    currentY += (splitName.length * 3.8) + 0.5;

    doc.text(splitGroup, margin, currentY);
    currentY += (splitGroup.length * 3.8) + 0.5;

    doc.text(`Santha Type: ${santhaTypeLabel}`, margin, currentY);
    currentY += 5;

    // TABLE
    // Proportional column sizing based on the safe content width
    const col0Width = contentWidth * 0.625; // 62.5% of printable width
    const col1Width = contentWidth * 0.375; // 37.5% of printable width

    doc.autoTable({
      startY: currentY,
      head: [tableColumn],
      body: tableRows,
      theme: "plain", // Light, ink-saving clean theme suited for thermal printers
      tableWidth: contentWidth, // Force the exact table width in mm to match divider lines
      styles: { 
        fontSize: 9.0, // Increased to 9.0pt for clear readability
        fontStyle: 'bold', // Force entire table text to print BOLD for maximum contrast
        cellPadding: 1.2, // Comfortable spacing
        textColor: [0, 0, 0],
        lineColor: [180, 180, 180], // Darker grid lines for easy reading
        lineWidth: 0.15, // Solid dividers
      },
      headStyles: { 
        fillColor: false, // Transparent headers (saves ink, avoids printer smudging)
        textColor: [0, 0, 0],
        fontSize: 9.5, // Increased to 9.5pt
        fontStyle: 'bold',
        halign: 'center',
        lineColor: [0, 0, 0],
        lineWidth: { bottom: 0.3, top: 0.3 } // Horizontal lines for headers
      },
      margin: { left: margin, right: margin }, // Align left and right boundaries
      columnStyles: { 
        0: { cellWidth: col0Width, halign: 'left' },
        1: { cellWidth: col1Width, halign: 'right' } 
      },
      // Dynamically make final financial totals bold for clarity
      didParseCell: function(data) {
        const rowDesc = data.row.cells[0]?.text?.[0] || "";
        if (["Total Collect", "To Be Collect", "Outstanding"].includes(rowDesc)) {
          data.cell.styles.fontStyle = "bold";
        }
      }
    });

    let finalY = doc.lastAutoTable.finalY || currentY;
    finalY += 5;

    doc.setFontSize(9.5); // Increased to 9.5pt
    doc.setFont("helvetica", "bold");
    doc.text(splitWords, margin, finalY);
    
    finalY += (splitWords.length * 4.2) + 5;
    
    // FOOTER - Very tight vertical padding
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5); // Increased to 9.5pt
    doc.text("Thank you for your payment!", pageWidth / 2, finalY, { align: "center" });

    finalY += 4.5;
    doc.setFont("helvetica", "bold"); // Bold administrative details
    doc.setFontSize(8.5); // Increased to 8.5pt
    doc.text("Administrative Office", pageWidth / 2, finalY, { align: "center" });
    finalY += 3.5;
    doc.setFontSize(8.0); // Increased to 8.0pt
    doc.text("Plot No-5 West, Marthupandiyar 2nd Cross St,", pageWidth / 2, finalY, { align: "center" });
    finalY += 3;
    doc.text("Viswanathapuram, Madurai - 625 014", pageWidth / 2, finalY, { align: "center" });
    finalY += 3;
    doc.text("Phone: +91 96000 35625", pageWidth / 2, finalY, { align: "center" });

    const pdfBlob = doc.output("blob");
    const pdfURL = URL.createObjectURL(pdfBlob);
    window.open(pdfURL, "_blank");
  };

  const columns = [
    { title: "S.No", render: (_, __, index) => index + 1 },
    {
      title: "ACCI Code",
      dataIndex: ["memberId", "Accno"],
      sorter: (a, b) => (a.memberId?.Accno || "").localeCompare(b.memberId?.Accno || ""),
    },
    {
      title: "Santha Type",
      dataIndex: "santhaname",
      render: (id) => santhaOptions.find((s) => s.value === id)?.label || "",
    },
    {
      title: "Member Name",
      dataIndex: "memberId",
      render: (member) => memberOptions.find((m) => m.value === member?._id)?.label || "",
    },
    {
      title: "Group Incharge",
      dataIndex: "memberId",
      render: (member) => memberOptions.find((m) => m.value === member?._id)?.groupincharge || "",
    },
    {
      title: "Admin Fee",
      dataIndex: "Adminfee",
      render: (value) => (parseFloat(value) || 0).toFixed(2),
    },
    {
      title: "Year Subscription",
      dataIndex: "yearsub",
      render: (value) => (parseFloat(value) || 0).toFixed(2),
    },
    {
      title: "Meeting Fee",
      dataIndex: "meetingfee",
      render: (value) => (parseFloat(value) || 0).toFixed(2),
    },
    {
      title: "Other",
      dataIndex: "other",
      render: (value) => (parseFloat(value) || 0).toFixed(2),
    },
    {
      title: "To Be Collect",
      dataIndex: "tobeCollect",
      render: (value) => (parseFloat(value) || 0).toFixed(2),
    },
    {
      title: "Outstanding",
      dataIndex: "outstanding",
      render: (value) => {
        const outstandingValue = parseFloat(value) || 0;
        const color = outstandingValue < 0 ? "green" : outstandingValue > 0 ? "red" : "black";
        return <span style={{ color }}>{outstandingValue.toFixed(2)}</span>;
      },
    },
    { title: "Remarks", dataIndex: "remarks" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
          />
          <Button
            icon={<PrinterOutlined />}
            onClick={() => printPDF(record)}
            title="Print A4"
          />
          <Button
            icon={<PrinterOutlined />}
            onClick={() => printPDFA5(record)}
            title="Print A5"
            style={{ color: "green" }}
          />
        </Space>
      ),
    },
  ];

  const normFile = (e) => (Array.isArray(e) ? e : []);

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Input
            placeholder="Search Santha"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Select
            placeholder="Filter by Santha Master"
            allowClear
            style={{ width: "100%" }}
            onChange={(value) => setSelectedSanthaFilter(value || null)}
            options={santhaOptions}
          />
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditMode(false);
              form.resetFields();
              form.setFieldsValue({ tobeCollect: "0.00", outstanding: "0.00", Adminfee: "0.00" });
              setIsModalVisible(true);
            }}
          >
            Add Santha
          </Button>
          <Button icon={<FilePdfOutlined />} onClick={exportPDF} style={{ marginLeft: 8 }}>
            Export PDF
          </Button>
          <Button icon={<FileExcelOutlined />} onClick={exportExcel} style={{ marginLeft: 8, color: "green" }}>
            Export Excel
          </Button>
        </Col>
      </Row>
      <Table columns={columns} dataSource={filteredData} rowKey="_id" />
      <Modal
        title={editMode ? "Edit Santha" : "Add Santha"}
        open={isModalVisible}
        onOk={() => form.validateFields().then(handleAddOrUpdate)}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        okButtonProps={{ loading }}
        width={800}
      >
        <Form form={form} layout="vertical">
          {/* Row 1 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Santha Type"
                name="santhaname"
                rules={[{ required: true, message: "Please select Santha Type" }]}
              >
                <Select options={santhaOptions} placeholder="Select Santha Type" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Member"
                name="memberId"
                rules={[{ required: true, message: "Please select Member" }]}
              >
                <Select
                  showSearch
                  placeholder="Select Member"
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  options={memberOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Group Incharge" name="groupincharge">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="To Be Collect"
                name="tobeCollect"
                rules={[
                  { required: true, message: "Please enter To Be Collect" },
                  { pattern: /^\d+(\.\d{1,2})?$/, message: "Please enter a valid number" },
                ]}
              >
                <Input type="number" step="0.01" min="0" />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 3 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Admin Fee"
                name="Adminfee"
                rules={[
                  { required: true, message: "Please enter Admin Fee" },
                  { pattern: /^\d+(\.\d{1,2})?$/, message: "Please enter a valid number" },
                ]}
              >
                <Input type="number" step="0.01" min="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Year Subscription"
                name="yearsub"
                rules={[
                  { required: true, message: "Please enter Year Subscription" },
                  { pattern: /^\d+(\.\d{1,2})?$/, message: "Please enter a valid number" },
                ]}
              >
                <Input type="number" step="0.01" min="0" />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Meeting Fee"
                name="meetingfee"
                rules={[
                  { required: true, message: "Please enter Meeting Fee" },
                  { pattern: /^\d+(\.\d{1,2})?$/, message: "Please enter a valid number" },
                ]}
              >
                <Input type="number" step="0.01" min="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Outstanding" name="outstanding">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 5 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Other"
                name="other"
                rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: "Please enter a valid number" }]}
              >
                <Input type="number" step="0.01" min="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Remarks" name="remarks">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 6 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Receipt"
                name="recipt"
                valuePropName="fileList"
                getValueFromEvent={normFile}
              >
                <Upload
                  beforeUpload={() => false}
                  listType="picture"
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>Upload Receipt</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Santha;